import { ALLOWED_ORIGIN_HOSTS, DEFAULT_DEV_ORIGIN } from "@/configs";
import { ENV, RUNTIME, SITE_URL, SITE_URL_NETWORK } from "@/constants";
import {
  EnvironmentEnum,
  LoggerLevelEnum,
  ProtocolEnum,
  RuntimeEnum,
} from "@/enums";
import { AppError } from "@/errors";
import type { Protocol } from "@/types";

// Extract the origin (scheme + host) from a URL string
export function toOrigin(url?: string | null): string | null {
  if (!url) return null;

  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

// Check if a hostname matches any of the given wildcard patterns
export function hasHostMatch(
  host: string,
  patterns: readonly string[],
): boolean {
  const h = host.toLowerCase();
  const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  for (const raw of patterns) {
    const p = raw.trim().toLowerCase();
    if (!p) continue;
    if (p === "*") return true;
    const rx = new RegExp(`^${p.split("*").map(esc).join(".*")}$`);
    if (rx.test(h)) return true;
  }
  return false;
}

// Normalize a raw protocol value to a valid Protocol enum member
function normalizeProtocol(
  raw: unknown,
  defaultProto: Protocol = ProtocolEnum.HTTP,
): Protocol {
  const p = String(raw ?? "").toLowerCase();
  return p === ProtocolEnum.HTTP
    ? ProtocolEnum.HTTP
    : p === ProtocolEnum.HTTPS
      ? ProtocolEnum.HTTPS
      : defaultProto;
}

// Derive an origin from request headers (x-forwarded-host/host)
export function originFromHeaders(
  headers: Headers,
  defaultProto: Protocol = ProtocolEnum.HTTP,
): string | null {
  const head = (v: string | null) => v?.split(",")[0]?.trim() || null;

  let host =
    head(headers.get("x-forwarded-host")) ?? headers.get("host") ?? null;
  if (!host) return null;
  host = host.trim();

  const proto = normalizeProtocol(
    head(headers.get("x-forwarded-proto")),
    defaultProto,
  );

  return `${proto}://${host}`;
}

// Derive an origin from the browser window.location (client only)
export function originFromWindow(
  defaultProto: Protocol = ProtocolEnum.HTTP,
): string {
  if (RUNTIME !== RuntimeEnum.CLIENT || !window.location) {
    throw new AppError(
      "ORIGIN_CLIENT_ONLY",
      "Origin from window is only available in client runtime",
    );
  }

  const url = new URL(window.location.href);

  const { host, protocol } = url;
  const proto = normalizeProtocol(protocol.replace(":", ""), defaultProto);

  return `${proto}://${host}`;
}

// Return a validated origin, falling back through site URL, network, and dev defaults
export function getSafeOrigin(
  origin: string | null,
  opts?: { devFallback?: string; allowedHosts?: string[] },
): string {
  const siteOrigin = toOrigin(SITE_URL);
  const networkOrigin = toOrigin(SITE_URL_NETWORK);
  const networkHost = networkOrigin ? new URL(networkOrigin).hostname : null;

  if (ENV === EnvironmentEnum.PRODUCTION) {
    if (siteOrigin) return siteOrigin;
    throw new AppError(
      "SITE_URL_INVALID",
      "SITE_URL must be a valid absolute URL (with protocol) in production.",
      { severity: LoggerLevelEnum.FATAL, meta: { siteUrl: SITE_URL } },
    );
  }

  const { devFallback, allowedHosts = ALLOWED_ORIGIN_HOSTS } = opts ?? {};
  if (origin) {
    const { hostname } = new URL(origin);
    const allowed =
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      isPrivateNetworkHost(hostname) ||
      (allowedHosts?.length ? hasHostMatch(hostname, allowedHosts) : true) ||
      (networkHost !== null && hostname === networkHost);
    if (allowed) return origin;
  }

  return (
    siteOrigin ?? networkOrigin ?? toOrigin(devFallback) ?? DEFAULT_DEV_ORIGIN
  );
}

// Shorthand: derive a safe origin from request headers
export function getSafeOriginFromHeaders(
  headers: Headers,
  opts?: { devFallback?: string; allowedHosts?: string[] },
): string {
  return getSafeOrigin(originFromHeaders(headers), opts);
}

// Shorthand: derive a safe origin from the browser window
export function getSafeOriginFromWindow(opts?: {
  devFallback?: string;
  allowedHosts?: string[];
}): string {
  return getSafeOrigin(originFromWindow(), opts);
}

// Check if a hostname is in a private network range (RFC 1918 / ULA)
export function isPrivateNetworkHost(hostname?: string | null): boolean {
  if (!hostname) return false;
  const h = hostname.toLowerCase();

  // IPv4
  const m = h.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (m) {
    const [a, b, c, d] = m.slice(1).map(Number);
    if ([a, b, c, d].some((n) => Number.isNaN(n) || n < 0 || n > 255))
      return false;

    if (a === 10) return true; // 10.0.0.0/8
    if (a === 172 && b >= 16 && b <= 31) return true; // 172.16.0.0/12
    if (a === 192 && b === 168) return true; // 192.168.0.0/16

    return false; // excludes 127/8, 169.254/16, 100.64/10 etc.
  }

  // IPv6 ULA fc00::/7 (fc.. or fd..)
  if (h.includes(":")) {
    return h.startsWith("fc") || h.startsWith("fd");
  }

  return false; // hostnames (incl. localhost) are not considered private network
}

// Check if an origin URL points to a private network host
export function isPrivateNetworkOrigin(origin?: string | null): boolean {
  if (!origin) return false;

  try {
    const u = new URL(origin);
    return isPrivateNetworkHost(u.hostname);
  } catch {
    return false;
  }
}
